/**
 * The primitives.
 *
 * shadcn/ui's token vocabulary (`--color-primary`, `--radius`) over plain elements rather than
 * Radix: the screens here are tables, forms and dialogs, and a native `<dialog>`, `<select>`
 * and `<table>` already have the keyboard behaviour and the accessible semantics that a
 * headless library would be pulled in to re-create.
 *
 * `docs/07-ui-screens.md` asks for keyboard-navigable tables, visible focus, labelled controls,
 * and contrast that survives the light/dark switch. Those are properties of these files.
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useEffect, useId, useRef } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import type { Tone } from '@/lib/policy';

// --------------------------------------------------------------------------- button -------

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  secondary: 'bg-secondary text-secondary-foreground hover:opacity-90 border border-border',
  ghost: 'hover:bg-accent text-foreground',
  destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md';
  busy?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  busy = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      // `aria-busy` rather than swapping the label for a spinner: a screen reader announces the
      // state without the button's name changing under the user mid-press.
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      className={cn(
        'focus-visible:ring-ring inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-opacity focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
        size === 'sm' ? 'h-8 px-3 text-sm' : 'h-9 px-4 text-sm',
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
}

/**
 * A link that looks like a button.
 *
 * A real `<a>`, not a button with an `onClick` that navigates: middle-click, ⌘-click and "open
 * in new tab" all work, and the browser shows the destination on hover.
 */
export function LinkButton({
  to,
  variant = 'secondary',
  size = 'md',
  className,
  children,
}: {
  to: string;
  variant?: Variant;
  size?: 'sm' | 'md';
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'focus-visible:ring-ring inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        size === 'sm' ? 'h-8 px-3 text-sm' : 'h-9 px-4 text-sm',
        VARIANTS[variant],
        className,
      )}
    >
      {children}
    </Link>
  );
}

/**
 * A plain `<a>` styled as a button, for a URL the *browser* must handle.
 *
 * Not `LinkButton`: react-router would intercept the click and try to render a route, so an
 * export would navigate to a blank page instead of downloading a file. The server's
 * `Content-Disposition` does the rest.
 */
export function DownloadButton({
  href,
  variant = 'secondary',
  size = 'md',
  className,
  children,
}: {
  href: string;
  variant?: Variant;
  size?: 'sm' | 'md';
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      className={cn(
        'focus-visible:ring-ring inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        size === 'sm' ? 'h-8 px-3 text-sm' : 'h-9 px-4 text-sm',
        VARIANTS[variant],
        className,
      )}
    >
      {children}
    </a>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'border-current/30 border-t-current inline-block size-3.5 animate-spin rounded-full border-2',
        className,
      )}
    />
  );
}

// ----------------------------------------------------------------------------- card -------

export function Card({
  title,
  action,
  children,
  className,
  footer,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  footer?: ReactNode;
}) {
  return (
    /*
     * Two classes here are load bearing on a phone, and both were measured rather than
     * guessed — at 390px the page scrolled sideways by 22px without them.
     *
     * `min-w-0`: a card is almost always a grid item, and a grid item's default
     * `min-width: auto` refuses to shrink below its content, so one wide table pushes the
     * whole page.
     * `overflow-hidden`: `Table` bleeds to the card's edge with a negative margin, which
     * otherwise escapes the card by exactly its padding. A modal `<dialog>` lives in the
     * browser's top layer, so this does not clip one.
     */
    <section
      className={cn('border-border bg-card min-w-0 overflow-hidden rounded-lg border', className)}
    >
      {title === undefined && action === undefined ? null : (
        <header className="border-border flex items-start justify-between gap-4 border-b px-5 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
      {footer ? <footer className="border-border border-t px-5 py-3">{footer}</footer> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------- badge -------

const TONES: Record<Tone, string> = {
  neutral: 'bg-secondary text-secondary-foreground',
  progress: 'bg-secondary text-secondary-foreground ring-1 ring-ring/40',
  good: 'text-[var(--color-success)] ring-1 ring-[var(--color-success)]/40',
  warn: 'text-[var(--color-warning)] ring-1 ring-[var(--color-warning)]/50',
  bad: 'text-destructive ring-1 ring-destructive/40',
};

export function Badge({
  tone = 'neutral',
  children,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------- alert -------

export function Alert({
  tone = 'warn',
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const border =
    tone === 'bad'
      ? 'border-destructive/50 text-destructive'
      : tone === 'warn'
        ? 'border-[var(--color-warning)]/50'
        : tone === 'good'
          ? 'border-[var(--color-success)]/50'
          : 'border-border';
  return (
    <div
      role={tone === 'bad' ? 'alert' : 'status'}
      className={cn('rounded-md border px-4 py-3 text-sm', border, className)}
    >
      {title ? <p className="font-medium">{title}</p> : null}
      {children ? <div className={cn(title ? 'mt-1' : '', 'text-sm')}>{children}</div> : null}
    </div>
  );
}

// ----------------------------------------------------------------------------- form -------

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode;
}) {
  const id = useId();
  const hintId = hint || error ? `${id}-hint` : undefined;
  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </label>
      {children({ id, 'aria-describedby': hintId })}
      {error ? (
        <p id={hintId} className="text-destructive text-xs">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-muted-foreground text-xs">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50';

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  // `h-9`, not just padding: two fields side by side in a grid stretch to the taller row, so a
  // neighbour with a two-line hint was making this input visibly taller than the others.
  return <input className={cn(CONTROL, 'h-9', className)} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(CONTROL, 'min-h-20', className)} {...rest} />;
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cn(CONTROL, 'h-9 pr-8', className)} {...rest} />;
}

export function Checkbox({
  label,
  hint,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className="flex gap-2.5">
      <input
        id={id}
        type="checkbox"
        className="border-input accent-primary focus-visible:ring-ring mt-0.5 size-4 shrink-0 rounded focus-visible:ring-2"
        {...rest}
      />
      <div className="grid gap-0.5">
        <label htmlFor={id} className="text-sm leading-snug">
          {label}
        </label>
        {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------- table -------

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  // Wide tables scroll inside their own container; the page body never scrolls sideways.
  return (
    <div className="-mx-5 overflow-x-auto px-5">
      <table className={cn('w-full border-collapse text-sm', className)}>{children}</table>
    </div>
  );
}

export function Th({
  children,
  numeric,
  className,
  ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={cn(
        'text-muted-foreground border-border border-b px-2 py-2 text-xs font-medium',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  numeric,
  className,
  ...rest
}: React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn(
        'border-border border-b px-2 py-2 align-top',
        numeric ? 'tabular text-right' : '',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

// -------------------------------------------------------------------------- dialog --------

/**
 * A native `<dialog>`, opened with `showModal()`.
 *
 * That one call is why this is not a div: the browser gives focus trapping, `Escape`, the
 * inert backdrop and the accessible role for free, and all four are things a hand-rolled modal
 * gets subtly wrong.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      className="bg-card text-card-foreground border-border m-auto w-[min(32rem,calc(100vw-2rem))] rounded-lg border p-0 shadow-lg backdrop:bg-black/40"
    >
      <div className="border-border border-b px-5 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div className="grid gap-4 p-5 text-sm">{children}</div>
      {footer ? (
        <div className="border-border flex justify-end gap-2 border-t px-5 py-3">{footer}</div>
      ) : null}
    </dialog>
  );
}

// ------------------------------------------------------------------------ empty state -----

export function EmptyState({
  title,
  children,
  action,
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="border-border rounded-md border border-dashed px-5 py-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      {children ? (
        <div className="text-muted-foreground mx-auto mt-2 max-w-prose text-sm">{children}</div>
      ) : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------- definition list ---

export function Facts({
  items,
  className,
}: {
  items: ReadonlyArray<{ label: ReactNode; value: ReactNode }>;
  className?: string;
}) {
  return (
    <dl className={cn('grid gap-x-4 gap-y-2 sm:grid-cols-[11rem_1fr]', className)}>
      {items.map((item, index) => (
        <div key={index} className="grid gap-x-4 sm:col-span-2 sm:grid-cols-subgrid">
          <dt className="text-muted-foreground text-sm">{item.label}</dt>
          <dd className="text-sm">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
